/**
 * Needs attention — admins only (the server sends `attention` to admins and
 * null to everyone else). A section headed like a module's but never
 * folded, always shown, with one box per check in a fixed order: its count, the period it
 * covers, a few examples where the problem has names, and a link to where it
 * is dealt with. A box with nothing to report stays, muted with a check, so
 * an all-clear is visible rather than an absent section.
 *
 * The periods are rolling, counted back from the server's now (the windows
 * in `dashboard/service.py`); the dates shown count back from the browser's.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Ban, CircleCheck, Factory, FlaskConical, MessagesSquare, ShieldAlert, TimerOff } from 'lucide-react';

import type { Attention } from '@/api/types';
import { opportunitiesHref } from '@/routes/business-opportunities/ideaFilters';
import { formatInt } from '@/ui/format';

import { DashboardModule } from './DashboardModule';

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

interface Check {
  key: string;
  icon: ReactNode;
  title: string;
  count: number;
  /** The period the count covers, e.g. "Last 7 days, since Sep 25". */
  window: string;
  /** A line under the count: what is behind it, or what was left out. */
  detail?: string;
  examples?: { id: string; label: string; to: string }[];
  to?: string;
  linkLabel?: string;
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

function since(ms: number, withTime: boolean): string {
  return new Date(Date.now() - ms).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(withTime ? { hour: 'numeric', minute: '2-digit' } : {}),
  });
}

function checks(a: Attention): Check[] {
  const events = a.denied_events + a.failed_events;
  const week = `Last 7 days, since ${since(WEEK_MS, false)}`;
  const day = `Last 24 hours, since ${since(DAY_MS, true)}`;
  return [
    {
      key: 'industries',
      icon: <Factory aria-hidden="true" />,
      title: 'Industries to approve',
      count: a.pending_industries.count,
      window: 'Right now',
      examples: a.pending_industries.examples.map((e) => ({ ...e, to: '/industries' })),
      to: '/industries',
      linkLabel: 'Review',
    },
    {
      key: 'research',
      icon: <FlaskConical aria-hidden="true" />,
      title: 'Failed research',
      count: a.failed_research.count,
      window: week,
      detail: 'Archived ideas left out',
      examples: a.failed_research.examples.map((e) => ({ ...e, to: `/business-opportunities/${e.id}` })),
      to: opportunitiesHref({ view: 'list', outcomes: ['failed'] }),
      linkLabel: 'Open',
    },
    {
      key: 'feedback',
      icon: <MessagesSquare aria-hidden="true" />,
      title: 'Feedback to triage',
      count: a.new_feedback.count,
      window: 'Right now',
      detail: 'New requests no admin has looked at',
      examples: a.new_feedback.examples.map((e) => ({ ...e, to: `/feedback/${e.id}` })),
      to: '/feedback?scope=all&status=new',
      linkLabel: 'Triage',
    },
    {
      key: 'refused',
      icon: <Ban aria-hidden="true" />,
      title: 'AI calls stopped by the budget',
      count: a.refused_runs,
      window: week,
      to: '/ai-usage',
      linkLabel: 'AI Usage',
    },
    {
      key: 'jobs',
      icon: <TimerOff aria-hidden="true" />,
      title: 'Stuck background jobs',
      count: a.stale_jobs,
      window: 'Right now',
      detail: 'Running, but no heartbeat lately',
    },
    {
      key: 'audit',
      icon: <ShieldAlert aria-hidden="true" />,
      title: 'Sign-in and request failures',
      count: a.failed_sign_ins + events,
      window: day,
      detail: `${formatInt(a.failed_sign_ins)} failed ${plural(a.failed_sign_ins, 'sign-in', 'sign-ins')} · ${formatInt(events)} denied or failed ${plural(events, 'request', 'requests')}`,
      to: '/audit-log',
      linkLabel: 'Audit Log',
    },
  ];
}

export function AttentionSection({ data }: { data: Attention }) {
  const all = checks(data);
  const open = all.filter((c) => c.count > 0).length;

  return (
    <DashboardModule
      id="attention"
      title="Needs attention"
      collapsible={false}
      aside={
        open > 0 ? (
          <span className="badge badge-warning">{formatInt(open)} to review</span>
        ) : (
          <span className="badge badge-success">All clear</span>
        )
      }
    >
      <div className="attention-grid">
        {all.map((c) => (
          <section key={c.key} className={`metric-tile attention-tile${c.count === 0 ? ' is-clear' : ''}`} aria-labelledby={`attention-${c.key}`}>
            <div className="attention-tile-head">
              <span className="attention-tile-icon">{c.count === 0 ? <CircleCheck aria-hidden="true" /> : c.icon}</span>
              <h3 id={`attention-${c.key}`}>{c.title}</h3>
            </div>
            <span className="metric-value">{formatInt(c.count)}</span>
            <span className="attention-tile-window">{c.window}</span>
            {c.detail && <span className="attention-tile-detail">{c.detail}</span>}
            {c.examples && c.examples.length > 0 && (
              <span className="attention-tile-examples">
                {c.examples.map((e) => (
                  <Link key={e.id} to={e.to}>
                    {e.label}
                  </Link>
                ))}
                {c.count > c.examples.length && <span>+{formatInt(c.count - c.examples.length)} more</span>}
              </span>
            )}
            {c.to && (
              <Link to={c.to} className="dash-more attention-tile-link">
                {c.linkLabel} <ArrowRight aria-hidden="true" />
              </Link>
            )}
          </section>
        ))}
      </div>
    </DashboardModule>
  );
}
